import { useEffect, useState } from 'react';
import { getCampaignDetail } from 'app/api/campaign';
import { getMallList } from 'app/api/mall';
import { getTierMaster } from 'app/api/tier';
import { LocalSessionKey } from 'app/constants/app-constants';
import { LocalStorage } from 'app/utils/storage';

// Both mall-list and tier-master responses vary in shape across the codebase
// (nested `.malls`/`.data` arrays vs a bare array) - normalize defensively.
function extractList(responseObj) {
  const data = responseObj?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.malls)) return data.malls;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function buildNameMap(list) {
  return Object.fromEntries(
    list.map((item) => [item.id, item.mallName || item.tierName || item.name])
  );
}

const useCampaignDetail = () => {
  const campaignData = JSON.parse(
    LocalStorage.get(LocalSessionKey.CAMPAIGN_DATA) || 'null'
  );

  const [pageLoading, setPageLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [mallMap, setMallMap] = useState({});
  const [tierMap, setTierMap] = useState({});

  useEffect(() => {
    const id = campaignData?.id;
    if (!id) return undefined;

    let cancelled = false;
    (async () => {
      setPageLoading(true);
      const [detailRes, mallRes, tierRes] = await Promise.all([
        getCampaignDetail(id),
        getMallList(),
        getTierMaster(),
      ]);
      if (cancelled) return;

      if (detailRes?.responseCode === 200) {
        setDetail(detailRes.data);
      }
      setMallMap(buildNameMap(extractList(mallRes)));
      setTierMap(buildNameMap(extractList(tierRes)));
      setPageLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One row per tierConfig entry: resolved mall/tier names, min spend, and the
  // vouchers scoped to that (mallId, tierId) pair - each carrying the
  // server-derived `remaining` count, never recomputed here.
  const targetingRows = (detail?.tierConfig || []).map((tierConfigEntry) => {
    const {
      mallId,
      tierId,
      tierName: serverTierName,
      minSpendThreshold,
      winsPerDay,
    } = tierConfigEntry;
    const rowVouchers = (detail?.vouchers || []).filter(
      (voucher) => voucher.mallId === mallId && voucher.tierId === tierId
    );

    // SC-6: prefer the server-supplied tierName (GamificationCampaignService,
    // Plan 01) over the below client-side name lookup, which is keyed on
    // tier_master.id (the CONCEPT id space) while tierConfig.tierId carries
    // Default_Tiers.id (the per-mall ROW id space) - see
    // src/utils/tierIdSpace.ts for the same mismatch documented backend-side.
    // Those are different id spaces, so the client-side lookup misses even
    // for real tiers, and for the Accrual sentinel (tierId 0) it renders the
    // raw number because no tier_master row has that id. null/undefined/''
    // all count as "not present" here (never a bare truthiness test on the
    // tier id itself, D-13) - the existing client-side fallback and the raw
    // tier id fallback below are both preserved unchanged.
    const hasServerTierName =
      serverTierName !== null &&
      serverTierName !== undefined &&
      serverTierName !== '';

    return {
      mallId,
      tierId,
      mallName: mallMap[mallId] || mallId,
      tierName: hasServerTierName ? serverTierName : tierMap[tierId] || tierId,
      minSpendThreshold,
      // winsPerDay is the server's per-pool daily cap, taken straight from
      // the tierConfig entry — same discipline as tierName/remaining above:
      // prefer the server value, never recompute it.
      winsPerDay,
      vouchers: rowVouchers,
    };
  });

  return { pageLoading, detail, mallMap, tierMap, targetingRows };
};

export default useCampaignDetail;
